import { ApiClient } from '@api/api-configuration';
import { Client } from '@api/READ_ONLY/client_api/Api';
import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import { APP_PREFIX, CLIENT_PAGE, EDIT_CLIENT } from '@app/routes/urls';
import QuickCreateClientModal from '@feat-clients/components/ClientTable/QuickCreateClientModal';
import { VisitLocation } from '@feat-scheduling/components/VisitCalendarEvent/VisitLocation';
import { Button, SelectProps, Tooltip } from 'antd';
import React from 'react';
import { AiOutlineUserAdd } from 'react-icons/ai';
import { FiEdit2 } from 'react-icons/fi';
import { GrLocation, GrPhone } from 'react-icons/gr';
import { useTranslation } from 'react-i18next';

/**
 * The fields this picker needs off a client to offer it as an option.
 *
 * Declared structurally, like `CaregiverChoice`, so a caller holding less than a full `Client`
 * — a visit's embedded client, say — can restrict this picker without inventing the rest of
 * the record.
 */
export interface ClientChoice {
  clientId: number;
  firstName: string;
  lastName: string;
}

type ClientSelectProps = {
  onClientChange?: (client: Client | Client[] | undefined) => void;
  placeholder?: string;
  pageSize?: number;
  minSearchLength?: number;
  searchDebounceMs?: number;
  selectedClientExternal?: Client | null;
  selectedItemsProvider?: () => Client | Client[] | undefined;
  /**
   * Restrict the picker to these clients, and these only.
   *
   * WHY THIS IS NOT A QUERY PARAMETER — the same reason `CaregiversSelectGeneric.restrictTo`
   * is not: `GET /clients` has no id filter, so "offer exactly these people" cannot be asked
   * of the server; and filtering the server's answer client-side does not work either,
   * because this picker PAGES — page 1 of a few thousand clients routinely contains none of
   * the wanted ones, so the dropdown would look empty while somebody eligible sat on page 40.
   *
   * So when this is supplied the picker stops fetching and serves from it, searched and paged
   * locally through the same {@link ApiFetchFunction} contract. The caller is expected to hold
   * a COMPLETE set, which is what makes local paging honest rather than a truncation.
   *
   * Two things change with it, both because a restricted option is NOT a fetched client:
   * the option row drops to the name alone (the phone and address were never fetched, and
   * printing "no phone" for a field nobody asked for would be a claim about the client), and
   * the quick-create button goes — a new client cannot be in a fixed set, so selecting one
   * would filter to somebody the caller has no rows for.
   *
   * `undefined`/`null` means no restriction: fetch, exactly as before. An EMPTY ARRAY is a
   * restriction to nobody and renders an empty dropdown, which is a real answer.
   */
  restrictTo?: readonly ClientChoice[] | null;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch'>;

// API fetch function for clients
const fetchClients: ApiFetchFunction<Client> = async (params: ApiQueryParams) => {
  // Exclude inactive clients from this selector (JSON:API filter syntax: filter[field][operator]=value).
  // Deceased clients are always inactive server-side, so this also excludes deceased clients.
  const apiParams = {
    ...params,
    sort: '-last_name',
    'filter[active][eq]': 'true',
  };
  return await ApiClient.clientApi.clients.listClients(apiParams);
};

/** Matches the free-text box against a client's name, the way a reader would expect. */
const matchesQuery = (client: ClientChoice, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return `${client.firstName} ${client.lastName}`.toLowerCase().includes(needle);
};

export const ClientSelectGeneric = ({
  onClientChange,
  placeholder,
  pageSize = 10,
  minSearchLength = 3,
  searchDebounceMs = 300,
  selectedClientExternal = null,
  selectedItemsProvider: externalProvider,
  restrictTo,
  ...selectProps
}: ClientSelectProps) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('clients-select-placeholder');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [lastCreatedClient, setLastCreatedClient] = React.useState<Client | null>(null);

  const isRestricted = restrictTo !== null && restrictTo !== undefined;

  /**
   * The restriction's identity — what has to change before the picker reloads.
   *
   * A caller derives `restrictTo` from data it holds, so it is a FRESH ARRAY on every render
   * even when the people in it are the same; keying on the ids is what keeps that from
   * rebuilding the fetcher on every unrelated render. `'api'` is the unrestricted case and is
   * a distinct value from `'r:'`, a restriction to NOBODY.
   */
  const restrictionKey = isRestricted ? `r:${restrictTo.map((client) => client.clientId).join(',')}` : 'api';

  // In a ref so the memo below can read the current set without listing the array itself as a
  // dependency, which would defeat the keying above.
  const restrictToRef = React.useRef(restrictTo);
  restrictToRef.current = restrictTo;

  /** One fetcher, either source, served through the SAME contract. */
  const apiFetch = React.useMemo<ApiFetchFunction<Client>>(
    () => async (params: ApiQueryParams) => {
      const allowed = restrictToRef.current;

      if (!allowed) {
        return fetchClients(params);
      }

      const matched = allowed.filter((client) => matchesQuery(client, params.query ?? ''));

      return {
        data: {
          // The fields this picker reads when restricted ARE on `ClientChoice`; the cast says
          // the rest of the record is knowingly absent rather than accidentally lost.
          data: matched.slice(params.offset, params.offset + params.limit) as unknown as Client[],
          // AFTER the search filter, which is what the component pages against.
          total: matched.length,
          offset: params.offset,
        },
      };
    },
    // The restriction itself is read through a ref, so its IDS are the dependency — the array
    // is a fresh object on every render and listing it would rebuild the fetcher constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restrictionKey]
  );

  return (
    <>
      <GenericSearchSelect<Client>
        {...selectProps}
        apiFetch={apiFetch}
        getItemId={(client) => client.clientId}
        getItemLabel={(client) => `${client.firstName} ${client.lastName}`}
        onSelectionChange={onClientChange}
        placeholder={resolvedPlaceholder}
        pageSize={pageSize}
        minSearchLength={minSearchLength}
        searchDebounceMs={searchDebounceMs}
        searchPlaceholder={t('clients-select-search-placeholder')}
        lazyLoadOnOpen
        selectedItemsProvider={() => {
          // Priority: external provider > last created > selected external
          const external = externalProvider?.();
          if (external) return external;
          if (lastCreatedClient) return [lastCreatedClient];
          if (selectedClientExternal) return [selectedClientExternal];
          return undefined;
        }}
        // Part of what the loaded page depends on, so a change to it has to reload — otherwise
        // a picker that opened before the restriction landed goes on offering the old set.
        reloadSignal={restrictionKey}
        renderOption={(client) =>
          isRestricted ? (
            // Name only. A restricted option carries the three fields of `ClientChoice` and
            // nothing else, so the phone and address rows below would be rendering absences.
            <span className="text-sm font-semibold">{`${client.firstName}, ${client.lastName}`}</span>
          ) : (
            <div className="flex justify-between items-center">
              <div className="flex flex-col text-sm">
                <span className="font-semibold">{`${client.firstName}, ${client.lastName}`}</span>
                <span className="text-xs flex items-center gap-2">
                  <GrPhone /> {client.phoneMobile || client.phoneHome || t('clients-select-no-phone')}
                </span>
                <span className="text-gray-400 text-xs flex items-center gap-2">
                  <GrLocation />
                  <VisitLocation
                    street={client.street || ''}
                    city={client.city || ''}
                    postalCode={client.postalCode || ''}
                  />
                </span>
              </div>
              <Tooltip title={t('clients-select-edit-tooltip')} placement="left">
                <Button
                  type="text"
                  size="small"
                  icon={<FiEdit2 className="text-base" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    const path = `/${APP_PREFIX}/${CLIENT_PAGE}/${EDIT_CLIENT.replace(':id', `${client.clientId}`)}`;
                    window.open(path, '_blank');
                  }}
                  className="ml-2 !bg-gray-100 hover:!bg-gray-200 !border-0"
                  style={{
                    backgroundColor: '#f3f4f6',
                  }}
                />
              </Tooltip>
            </div>
          )
        }
        labelRender={(option) => {
          return <span>{option.label}</span>;
        }}
        // No quick-create on a restricted picker: the new client cannot be in the fixed set,
        // so selecting it would point the caller at somebody it holds no rows for.
        inputSearchProps={
          isRestricted
            ? undefined
            : {
                suffix: (
                  <Tooltip title={t('clients-select-create-tooltip')}>
                    <Button
                      type="primary"
                      icon={<AiOutlineUserAdd className="text-base" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCreateOpen(true);
                      }}
                    />
                  </Tooltip>
                ),
              }
        }
      />
      <QuickCreateClientModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onCreated={(client) => {
          setCreateOpen(false);
          setLastCreatedClient(client);
          onClientChange?.(client);
        }}
      />
    </>
  );
};

export default ClientSelectGeneric;
